/**
 * Official Paranormal Archetypes content.
 * Source: Paranormal Archetype Guidebook (Archetype Reference Cards) and Starter Kit.
 * All text is transcribed VERBATIM. Do not paraphrase, expand, or invent content.
 *
 * NOTE: The Energetic Architect reference card in the Guidebook has a source header
 * typo (titled "The Grid Technician" with Core Orientation "Data • Instrumentation •
 * Verification"). Its body content (Core Message, Role, Strengths, Shadow, emblem) is
 * clearly the Energetic Architect and is used here. The Energetic Architect's Core
 * Orientation keywords are left null pending official confirmation.
 */

const EMBLEMS = {
  'channel-seer': '/images/archetypes/channel-seer.png',
  'chronicle-keeper': '/images/archetypes/chronicle-keeper.png',
  'energetic-architect': '/images/archetypes/energetic-architect.png',
  'grid-technician': '/images/archetypes/grid-technician.png',
  'resonance-empath': '/images/archetypes/resonance-empath.png',
  'shadow-watcher': '/images/archetypes/shadow-watcher.png',
  'skeptic-sentinel': '/images/archetypes/skeptic-sentinel.png',
  'spirit-communicator': '/images/archetypes/spirit-communicator.png',
  'threshold-walker': '/images/archetypes/threshold-walker.png',
};

export const ARCHETYPE_CONTENT = {
  'Channel-Seer': {
    name: 'The Channel-Seer',
    slug: 'channel-seer',
    emblem: EMBLEMS['channel-seer'],
    coreOrientation: 'Perception • Reception • Insight',
    coreMessage: 'Information arrives when the channel is clear.',
    role:
      'The Channel-Seer experiences the paranormal as information rather than emotion. Their awareness functions like an open receiver, allowing impressions, symbols, phrases, or complete understandings to arrive internally without force or interpretation.\n\nRather than reacting to activity or attempting to initiate interaction, the Channel-Seer notices what is already present. Insight arrives whole, often without context, and reveals its meaning later through timing, confirmation, or unfolding events.\n\nIn investigative environments, the Channel-Seer often provides clarity during moments of uncertainty, translating subtle impressions into usable understanding without emotional amplification or dramatic response.',
    primaryStrengths: [
      'Receives clear, non-emotional informational impressions',
      'Recognizes insight without needing validation',
      'Translates subtle awareness into language others can understand',
      'Remains calm during heightened or chaotic moments',
      'Allows communication to occur without interference',
    ],
    shadowTendency:
      'May doubt their own perceptions, over-interpret ordinary thoughts as messages, or struggle to articulate insight to others. When ungrounded, the Channel-Seer may disconnect from context or lose confidence in discernment.',
    bestTeamContribution: [
      'Translating impressions into clear language',
      'Providing insight without emotional escalation',
      'Recognizing when communication has completed',
      'Supporting teams with clarity during uncertain moments',
      'Acting as a conduit rather than an interpreter',
    ],
    interfacesWithUnknown:
      'The Channel-Seer does not initiate communication. They receive.\n\nBy remaining neutral, open, and grounded, the Channel-Seer allows information to arrive cleanly, without distortion, projection, or narrative overlay. Their strength lies not in volume or frequency of messages, but in clarity and precision.',
    symbolAlignment: [
      'Single open eye / aperture: perception beyond physical sight',
      'Radiating lines or arcs: signal reception rather than transmission',
      'Vertical alignment: connection across layers of awareness',
      'Minimal framing: openness, sensitivity, and non-interference',
      'Soft symmetry: clarity without force',
    ],
    regulationNeed: 'Sensory containment',
    grounding: [
      'Quiet intervals before interpretation',
      'Reduced visual and auditory stimulation',
      'Clear start-and-stop boundaries around reception',
    ],
    workingWithThem: 'Reduce noise. Don\u2019t demand immediate explanation. Timing matters.',
  },

  'Chronicle Keeper': {
    name: 'The Chronicle Keeper',
    slug: 'chronicle-keeper',
    emblem: EMBLEMS['chronicle-keeper'],
    coreOrientation: 'Memory • Continuity • Context',
    coreMessage: 'What remains matters.',
    role:
      'The Chronicle Keeper experiences the paranormal through history, memory, and residual imprint. They naturally assess environments by sensing what has accumulated over time, events, emotions, stories, and patterns that linger beyond their moment of origin.\n\nRather than focusing on isolated phenomena, the Chronicle Keeper interprets how present activity connects to past moments, noticing when experiences feel residual, cyclical, or historically anchored rather than spontaneous or interactive.\n\nIn investigations, the Chronicle Keeper often becomes the context holder, ensuring that activity is understood within its broader narrative rather than misinterpreted as isolated or sensational.',
    primaryStrengths: [
      'Recognizes repeating patterns across time and location',
      'Identifies historical context influencing present activity',
      'Distinguishes residual memory from intelligent interaction',
      'Preserves accuracy through documentation and research',
      'Adds narrative clarity to complex or layered environments',
    ],
    shadowTendency:
      'May over-focus on documentation or historical validation at the expense of present-moment perception. Can feel burdened by emotional residue from historically dense locations or frustrated when context is overlooked or dismissed.',
    bestTeamContribution: [
      'Conducting historical research and timeline reconstruction',
      'Organizing case notes, records, and investigative continuity',
      'Identifying narrative threads across multiple investigations',
      'Supporting skeptical or analytical archetypes with context',
      'Preventing misinterpretation of residual phenomena',
    ],
    interfacesWithUnknown:
      'The Chronicle Keeper does not attempt to provoke or alter paranormal activity. Instead, they witness and preserve it.\n\nBy holding memory, timeline, and context, the Chronicle Keeper allows the unknown to be understood as part of an unfolding record, rather than reduced to isolated moments or unexplained reactions.',
    symbolAlignment: [
      'Open book / layered pages: memory, documentation, and continuity',
      'Central eye or seal: witness consciousness rather than participant interference',
      'Subtle timeline markings: historical context and pattern tracking across investigations',
      'Framing border: preservation, accuracy, and narrative integrity',
      'Light source from within: meaning revealed through reflection, not reaction',
    ],
    regulationNeed: 'Cognitive sequencing',
    grounding: [
      'Documentation during or immediately after events',
      'Clear timelines and contextual markers',
      'Separation of observation from interpretation',
    ],
    workingWithThem: 'Don\u2019t pressure them to interpret. Their role is preservation, not conclusion.',
  },

  'Energetic Architect': {
    name: 'The Energetic Architect',
    slug: 'energetic-architect',
    emblem: EMBLEMS['energetic-architect'],
    coreOrientation: 'Structure • Systems • Foundation',
    coreMessage: 'Structure determines what can exist.',
    role:
      'The Energetic Architect experiences the paranormal through structure, systems, and operational integrity. They naturally assess environments by noticing frameworks, patterns, inconsistencies, and points of instability.\n\nRather than focusing on phenomena themselves, the Architect evaluates how an investigation is built, how information is gathered, and whether the structure supporting the experience can sustain clarity and accuracy.\n\nThe Architect often becomes the stabilizing force in environments that feel chaotic, emotionally charged, or operationally fragmented.',
    primaryStrengths: [
      'Establishes clear investigative frameworks',
      'Identifies structural or procedural weaknesses',
      'Grounds teams during intense or unpredictable activity',
      'Creates documentation, systems, and repeatable processes',
      'Brings order and clarity to complex environments',
    ],
    shadowTendency:
      'May over-prioritize control, dismiss unstructured intuitive data, or struggle in environments where systems are incomplete, fluid, or undefined. Can become frustrated when structure is ignored or undermined.',
    bestTeamContribution: [
      'Designing investigation protocols and workflows',
      'Creating documentation and data organization systems',
      'Supporting intuitive archetypes by providing grounding structure',
      'Maintaining stability during emotionally heightened investigations',
      'Acting as the backbone that allows other archetypes to function effectively',
    ],
    interfacesWithUnknown:
      'The Energetic Architect does not seek to explain the paranormal itself. Instead, they define how humans interface with it.\n\nBy strengthening structure, the Architect allows clearer perception, safer exploration, and more reliable interpretation of anomalous environments.',
    symbolAlignment: [
      'Geometric framework / lattice: sensing, mapping, and stabilizing underlying energetic structures',
      'Interlocking lines: systems thinking; understanding how energy flows between people, places, and moments',
      'Balanced symmetry: order within chaos; pattern recognition over emotional reaction',
      'Central anchor point: grounding volatile environments and anchoring investigations',
      'Enclosed form: containment, reinforcement, and energetic integrity',
    ],
    regulationNeed: 'Structural grounding',
    grounding: [
      'Physical anchoring (standing, sitting, contact with solid surfaces)',
      'Deliberate pauses between environmental shifts',
      'Reducing responsibility for others\u2019 regulation',
    ],
    workingWithThem: 'Give space. Don\u2019t rush. Let stabilization occur before pushing forward.',
  },

  'Grid Technician': {
    name: 'The Grid Technician',
    slug: 'grid-technician',
    emblem: EMBLEMS['grid-technician'],
    coreOrientation: 'Data • Instrumentation • Verification',
    coreMessage: 'Data reveals patterns when properly measured.',
    role:
      'The Grid Technician experiences the paranormal through measurement, instrumentation, and data correlation. They focus on signal behavior, environmental variables, and the relationship between inputs and recorded outputs.\n\nRather than interpreting phenomena subjectively, the Grid Technician evaluates whether data is consistent, repeatable, and contextually sound. They are attuned to fluctuations, anomalies, interference, and environmental conditions that may affect readings.\n\nIn investigations, the Grid Technician often serves as the grounding bridge between experience and evidence, ensuring that observations are supported by reliable data pathways.',
    primaryStrengths: [
      'Operates and calibrates investigative equipment with precision',
      'Identifies signal anomalies, interference, and environmental variables',
      'Establishes reliable data collection methods',
      'Correlates multiple data streams for validation',
      'Supports credibility and repeatability in investigations',
    ],
    shadowTendency:
      'May over-rely on instrumentation, dismissing experiential or intuitive input. Can struggle in environments where data is sparse, ambiguous, or inconclusive, or where phenomena do not register through measurable means.',
    bestTeamContribution: [
      'Managing technical equipment and monitoring systems',
      'Ensuring accuracy, calibration, and consistency of data',
      'Identifying false positives or environmental interference',
      'Supporting intuitive archetypes by validating patterns post-event',
      'Strengthening investigative credibility through data integrity',
    ],
    interfacesWithUnknown:
      'The Grid Technician does not seek to define the paranormal. They seek to understand how it registers.\n\nBy focusing on measurable interaction points, the Grid Technician helps teams differentiate between noise and meaningful signal, allowing clearer analysis without prematurely assigning interpretation.',
    symbolAlignment: [
      'Node-and-line network: instrumentation pathways and data flow',
      'Intersections: correlation points between environment and response',
      'Technical symmetry: calibration, consistency, and repeatability',
      'Structured perimeter: boundaries between observation and interpretation',
      'Central node: reference point for validation',
    ],
    regulationNeed: 'Verification anchoring',
    grounding: [
      'Reference points (data logs, calibration tools)',
      'Clear methodological steps',
      'Time to confirm patterns without interruption',
    ],
    workingWithThem: 'Let them work uninterrupted. Don\u2019t force intuition-based decisions onto them.',
  },

  'Resonance Empath': {
    name: 'The Resonance Empath',
    slug: 'resonance-empath',
    emblem: EMBLEMS['resonance-empath'],
    coreOrientation: 'Resonance • Sensitivity • Attunement',
    coreMessage: 'Awareness begins with what is felt.',
    role:
      'The Resonance Empath experiences the paranormal through emotional, atmospheric, and energetic resonance. They naturally register subtle shifts in mood, pressure, tone, and internal response before phenomena become externally noticeable.\n\nRather than focusing on evidence or interpretation, the Empath becomes aware of how an environment feels, how it impacts the nervous system, emotional state, and internal balance of those present.\n\nIn investigations, the Resonance Empath often serves as an early indicator, registering changes that others may not consciously notice yet. Their sensitivity allows teams to recognize when environments shift, escalate, or require grounding.',
    primaryStrengths: [
      'Detects subtle emotional or energetic changes',
      'Senses atmospheric shifts before overt activity',
      'Helps teams recognize when environments become destabilizing',
      'Maintains attunement between self, team, and location',
      'Offers insight into how environments are experienced, not just observed',
    ],
    shadowTendency:
      'May absorb environmental or emotional intensity too deeply, leading to overwhelm, fatigue, or blurred boundaries. Can struggle to distinguish between personal response and external influence if grounding practices are not maintained.',
    bestTeamContribution: [
      'Monitoring emotional and energetic climate during investigations',
      'Alerting teams to subtle shifts before escalation',
      'Supporting balance and regulation in intense environments',
      'Helping teams pace investigations sustainably',
      'Acting as a barometer for environmental impact',
    ],
    interfacesWithUnknown:
      'The Resonance Empath does not seek to interpret or communicate with the unknown directly. Instead, they register how the unknown affects human perception.\n\nBy recognizing resonance patterns, the Empath helps teams understand when environments influence emotional state, awareness, or stability, allowing investigations to remain conscious, grounded, and respectful.',
    symbolAlignment: [
      'Waveforms or concentric rings: emotional and energetic feedback patterns',
      'Soft curves: sensitivity, adaptability, and non-forceful engagement',
      'Central pulse: internal regulation before external response',
      'Balanced spacing: boundaries maintained within connection',
      'Flowing continuity: attunement over interpretation',
    ],
    regulationNeed: 'Boundary reinforcement',
    grounding: [
      'Deliberate emotional separation after exposure',
      'Brief withdrawal from group dynamics',
      'Internal regulation before external discussion',
    ],
    workingWithThem: 'Check capacity. Encourage regulation before analysis.',
  },

  'Shadow Watcher': {
    name: 'The Shadow Watcher',
    slug: 'shadow-watcher',
    emblem: EMBLEMS['shadow-watcher'],
    coreOrientation: 'Observation • Restraint • Peripheral Awareness',
    coreMessage: 'What is absent can be as meaningful as what appears.',
    role:
      'The Shadow Watcher experiences the paranormal through subtle observation, absence, and peripheral awareness. They are attuned to what shifts quietly, changes in atmosphere, gaps in expectation, distortions, or movements that do not demand attention but subtly alter perception.\n\nRather than confronting phenomena directly, the Shadow Watcher observes from a place of restraint. They notice patterns others overlook, especially those that emerge only when pressure is removed.\n\nIn investigations, the Shadow Watcher often serves as the quiet sentinel, maintaining awareness without interference.',
    primaryStrengths: [
      'Detects subtle environmental shifts and anomalies',
      'Maintains calm presence in uncertain or tense conditions',
      'Notices patterns in absence, silence, or distortion',
      'Observes without escalating or influencing activity',
      'Supports team awareness through patient vigilance',
    ],
    shadowTendency:
      'May remain too distant, withholding insights or delaying communication. Can be overlooked or underestimated due to their quiet observational style. May struggle when direct action or confrontation is required.',
    bestTeamContribution: [
      'Monitoring environments for subtle or delayed changes',
      'Identifying patterns missed by direct engagement',
      'Maintaining awareness during long or quiet investigations',
      'Supporting team safety through calm observation',
      'Providing insight after events rather than during escalation',
    ],
    interfacesWithUnknown:
      'The Shadow Watcher does not provoke or pursue the unknown. They allow it to reveal itself.\n\nBy remaining present without pressure, the Shadow Watcher creates space for subtle phenomena to emerge, offering insight through patience rather than pursuit.',
    symbolAlignment: [
      'Negative space shapes: attention to absence, distortion, and subtle movement',
      'Peripheral framing: observation without confrontation',
      'Muted symmetry: patience, restraint, and vigilance',
      'Dark-light contrast: boundary perception rather than illumination',
      'Still center: calm presence in uncertain conditions',
    ],
    regulationNeed: 'Stillness and distance',
    grounding: [
      'Minimal stimulation',
      'Delayed articulation until clarity forms',
      'Permission to observe without pressure',
    ],
    workingWithThem: 'Invite input later. Don\u2019t force immediate articulation.',
  },

  'Skeptic Sentinel': {
    name: 'The Skeptic Sentinel',
    slug: 'skeptic-sentinel',
    emblem: EMBLEMS['skeptic-sentinel'],
    coreOrientation: 'Discernment • Validation • Protection',
    coreMessage: 'Clarity requires evidence before interpretation.',
    role:
      'The Skeptic Sentinel experiences the paranormal through evaluation, testing, and methodological discipline. They naturally assess environments by identifying assumptions, inconsistencies, cognitive bias, and unsupported conclusions.\n\nRather than reacting to phenomena or narratives, the Sentinel focuses on what can be verified, replicated, or responsibly ruled out. Their presence ensures that investigations remain grounded, credible, and ethically sound.\n\nThe Skeptic Sentinel often becomes the stabilizing counterbalance in environments that are emotionally charged, belief-driven, or vulnerable to premature conclusions.',
    primaryStrengths: [
      'Applies critical thinking under pressure',
      'Identifies bias, assumption, and contamination risks',
      'Strengthens credibility through verification and control',
      'Maintains investigative discipline and ethical boundaries',
      'Protects teams from narrative drift and misinterpretation',
    ],
    shadowTendency:
      'May become overly dismissive, slow to engage with experiential data, or resistant to perspectives that cannot be immediately measured. Can unintentionally shut down valuable exploratory dialogue if balance is lost.',
    bestTeamContribution: [
      'Evaluating evidence and methodology',
      'Establishing controls and validation procedures',
      'Challenging assumptions constructively',
      'Supporting documentation accuracy and integrity',
      'Ensuring conclusions are earned, not assumed',
    ],
    interfacesWithUnknown:
      'The Skeptic Sentinel does not seek to deny the paranormal. Instead, they safeguard the process by which it is explored.\n\nBy insisting on clarity, testing, and intellectual honesty, the Sentinel creates conditions where genuine anomalies can be distinguished from error, bias, or expectation.',
    symbolAlignment: [
      'Shield or gate form: protection against assumption and bias',
      'Balanced geometry: logic, testing, and methodological rigor',
      'Central lens or marker: focus on evidence over narrative',
      'Strong edges: discernment, not dismissal',
      'Grounded symmetry: stability under pressure',
    ],
    regulationNeed: 'Intellectual stability',
    grounding: [
      'Clear criteria for evaluation',
      'Space to articulate concerns without defensiveness',
      'Avoiding forced consensus',
    ],
    workingWithThem: 'Don\u2019t interpret caution as resistance. Their role protects integrity.',
  },

  'Spirit Communicator': {
    name: 'The Spirit Communicator',
    slug: 'spirit-communicator',
    emblem: EMBLEMS['spirit-communicator'],
    coreOrientation: 'Connection • Dialogue • Ethical Interface',
    coreMessage: 'Communication requires respect.',
    role:
      'The Spirit Communicator experiences the paranormal as relational and responsive. They naturally perceive the unseen as conscious, interactive, and capable of engagement rather than as passive phenomena.\n\nRather than focusing on data collection or structural analysis, the Spirit Communicator senses when presence becomes personal, when something is listening, responding, or seeking acknowledgment.\n\nIn investigative environments, this archetype often becomes the point where activity shifts from atmospheric to interactive. Their presence can transform a location from reactive to conversational, grounding communication in intention rather than provocation.',
    primaryStrengths: [
      'Establishes respectful, grounded communication with conscious presence',
      'Discerns between residual activity and intelligent interaction',
      'Creates ethical tone and pacing during EVP or spirit-box sessions',
      'Calms environments by acknowledging presence without escalation',
      'Helps teams move from chaos or fear into clarity and dialogue',
    ],
    shadowTendency:
      'May over-identify with presences, struggle with energetic boundaries, or feel responsible for maintaining connection. Can experience depletion if grounding and closure are neglected, or self-doubt if communication is questioned by others.',
    bestTeamContribution: [
      'Leading EVP, spirit-box, or call-and-response sessions',
      'Setting ethical tone for communication and engagement',
      'Supporting sensitive teammates during intense interactions',
      'Helping teams recognize when to engage, and when to disengage',
      'Acting as a relational stabilizer when activity becomes personal',
    ],
    interfacesWithUnknown:
      'The Spirit Communicator does not seek control, proof, or dominance. Instead, they create conditions where communication can occur safely, respectfully, and intentionally.\n\nBy acknowledging presence without sensationalism, the Spirit Communicator allows interaction to unfold without fear, coercion, or overreach, maintaining ethical boundaries while remaining receptive.',
    symbolAlignment: [
      'Central skull: direct interface with consciousness across thresholds',
      'Flowing breath or wisps: messages carried rather than forced',
      'Two spirits: dialogue and mutual recognition, not domination',
      'Key: access granted through respect and attunement',
      'Open hands: receiver role rather than authority',
      'Circular seal: containment, protection, and ethical boundaries',
    ],
    regulationNeed: 'Ethical containment',
    grounding: [
      'Clear intent setting before engagement',
      'Defined ethical limits',
      'Time to disengage and reset after dialogue',
    ],
    workingWithThem: 'Maintain structure. Don\u2019t allow domination or overreach.',
  },

  'Threshold Walker': {
    name: 'The Threshold Walker',
    slug: 'threshold-walker',
    emblem: EMBLEMS['threshold-walker'],
    coreOrientation: 'Liminality • Transition • Passage',
    coreMessage: 'Not all movement is meant to be seen, some is meant to be held.',
    role:
      'The Threshold Walker experiences the paranormal through transition, boundary states, and moments of passage rather than fixed manifestations.\n\nThey are most attuned to spaces where movement is occurring: entrances, exits, pauses, beginnings, endings, and the quiet moments between activity. Rather than focusing on presence alone, the Threshold Walker senses when something is arriving, leaving, unresolved, or shifting states.\n\nIn investigations, this archetype often becomes the calm anchor during moments of uncertainty, change, or energetic transition, holding space when others rush toward conclusions.',
    primaryStrengths: [
      'Sensing activity during transitions rather than peak moments',
      'Detecting energetic boundaries and crossings',
      'Remaining grounded during uncertainty or unresolved activity',
      'Holding calm presence during escalations or endings',
      'Recognizing when an investigation or interaction is incomplete',
    ],
    shadowTendency:
      'May linger too long in uncertainty, resist closure, or feel suspended between decisions. Can struggle with grounding after prolonged exposure to transitional environments or unfinished energetic states.',
    bestTeamContribution: [
      'Recognizing when energy is shifting rather than static',
      'Preventing rushed conclusions or premature escalation',
      'Holding space during beginnings, endings, and unresolved moments',
      'Supporting safe entry and exit from intense environments',
      'Acting as a stabilizing presence during uncertainty',
    ],
    interfacesWithUnknown:
      'The Threshold Walker does not seek control, confrontation, or explanation. Instead, they hold space for movement.\n\nBy remaining present during transitions, the Threshold Walker allows the unseen to resolve, withdraw, or emerge naturally, often revealing information that appears only after others disengage.',
    symbolAlignment: [
      'Doorway / arch motif: perception of liminal awareness and transition points',
      'Split forms: existing between states without attachment',
      'Balanced openness: entry without intrusion',
      'Forward motion cue: timing, readiness, and movement',
      'Soft containment: safety while crossing boundaries',
    ],
    regulationNeed: 'Transitional anchoring',
    grounding: [
      'Ritualized entry and exit from investigative phases',
      'Physical movement to mark transitions',
      'Clear role boundaries between phases',
    ],
    workingWithThem: 'Allow them to manage pacing. Don\u2019t trap them in static roles.',
  },
};

// Common Archetype Interaction Patterns (Starter Kit, Ch. 8), verbatim
export const INTERACTION_PATTERNS = [
  {
    pair: ['Energetic Architect', 'Grid Technician'],
    friction: 'Structure vs. instrumentation',
    points: [
      'The Architect focuses on environmental stability and containment',
      'The Technician focuses on calibration, readings, and repeatability',
    ],
    misalignment: [
      'The Architect feels data collection ignores energetic instability',
      'The Technician feels structure lacks measurable support',
    ],
    resolution: 'Structure stabilizes conditions; instrumentation confirms patterns. Neither replaces the other.',
  },
  {
    pair: ['Channel-Seer', 'Skeptic Sentinel'],
    friction: 'Perception vs. verification',
    points: [
      'The Channel-Seer receives impressions without explanation',
      'The Skeptic Sentinel requires methodological grounding',
    ],
    misalignment: ['Perception is dismissed prematurely', 'Skepticism becomes adversarial rather than evaluative'],
    resolution: 'Perception initiates inquiry; skepticism refines it.',
  },
  {
    pair: ['Resonance Empath', 'Shadow Watcher'],
    friction: 'Emotional absorption vs. silent vigilance',
    points: ['The Empath processes atmosphere internally', 'The Shadow Watcher observes without expression'],
    misalignment: [
      'The Empath interprets silence as detachment',
      'The Watcher interprets emotional response as interference',
    ],
    resolution: 'One senses impact; the other monitors risk. Both are protective functions.',
  },
  {
    pair: ['Spirit Communicator', 'Threshold Walker'],
    friction: 'Engagement vs. timing',
    points: ['The Communicator seeks respectful interaction', 'The Walker senses readiness and transition points'],
    misalignment: ['Interaction is initiated too early', 'Pauses are mistaken for avoidance'],
    resolution: 'Communication depends on timing. Readiness precedes exchange.',
  },
  {
    pair: ['Chronicle Keeper', 'Everyone Else'],
    friction: 'Documentation vs. momentum',
    points: ['The Chronicle Keeper records rather than reacts', 'Others may want immediate interpretation or action'],
    misalignment: ['Documentation is mistaken for disengagement', 'The Keeper is pressured to interpret prematurely'],
    resolution: 'Meaning emerges over time. Preservation enables later clarity.',
  },
];

// Team balance considerations (Starter Kit, Ch. 8), verbatim
export const TEAM_PRINCIPLES = {
  intro:
    'Paranormal archetypes aren\u2019t personality matches. They\u2019re functional lenses.\n\nTeams don\u2019t succeed because everyone processes the same way. They succeed because different processing styles cover blind spots.',
  groundRules: [
    'No archetype interprets another\u2019s data',
    'Perception is recorded before evaluation',
    'Validation occurs after collection, not during',
    'Regulation is handled individually, not enforced collectively',
  ],
};

export function getArchetypeContent(name) {
  if (!name) return null;
  return ARCHETYPE_CONTENT[name] || null;
}

export function getEmblem(name) {
  return getArchetypeContent(name)?.emblem;
}

// Returns interaction patterns relevant to a given archetype (its specific pairing(s)
// plus the universal "Chronicle Keeper + Everyone Else" pattern).
export function getInteractionPatternsFor(name) {
  if (!name) return [];
  const result = [];
  for (const p of INTERACTION_PATTERNS) {
    if (p.pair.includes(name) || (p.pair[1] === 'Everyone Else' && name !== 'Chronicle Keeper')) {
      result.push(p);
    }
  }
  return result;
}
